/**
 * Sanitization utilities for preventing XSS and HTML injection attacks
 */

import sanitizeHtml from 'sanitize-html';

/**
 * Sanitizes HTML content by removing all HTML tags and leaving only plain text.
 * Uses the sanitize-html library with strictest settings to prevent XSS attacks.
 *
 * @param unsafeString - The potentially unsafe string to sanitize
 * @returns The sanitized string with all HTML removed
 *
 * @example
 * ```typescript
 * const userInput = '<script>alert("XSS")</script>';
 * const safe = sanitizeHtmlContent(userInput);
 * // Returns: ''
 * ```
 */
export function sanitizeHtmlContent(unsafeString: string): string {
  if (!unsafeString) {
    return unsafeString;
  }

  // Remove all HTML tags and dangerous content
  return sanitizeHtml(unsafeString, {
    allowedTags: [], // No HTML tags allowed
    allowedAttributes: {}, // No attributes allowed
    disallowedTagsMode: 'discard' // Discard disallowed tags entirely
  });
}

/**
 * Validates that a GUID/accountId parameter contains only allowed characters.
 * GUIDs should only contain alphanumeric characters and hyphens.
 * This prevents injection of special characters, HTML tags, or scripts.
 *
 * @param value - The value to validate
 * @returns True if the value contains only valid GUID characters, false otherwise
 *
 * @example
 * ```typescript
 * isValidGuidFormat('12345678-1234-1234-1234-123456789012'); // true
 * isValidGuidFormat('<script>alert("xss")</script>'); // false
 * isValidGuidFormat('test-account-id'); // true
 * isValidGuidFormat('account/test'); // false - contains /
 * ```
 */
export function isValidGuidFormat(value: string): boolean {
  if (!value) {
    return false;
  }

  // GUID format: alphanumeric and hyphens only
  // No special characters, HTML tags, slashes, or newlines allowed
  const guidPattern = /^[a-zA-Z0-9-]+$/;
  return guidPattern.test(value);
}

/**
 * Sanitizes a URL parameter value by removing HTML and validating format.
 * This is specifically designed for accountId and similar GUID parameters
 * that should not contain HTML or special characters.
 *
 * @param paramValue - The parameter value to sanitize
 * @returns The sanitized parameter value, or empty string if invalid
 *
 * @example
 * ```typescript
 * sanitizeUrlParameter('valid-account-123'); // 'valid-account-123'
 * sanitizeUrlParameter('<b>test</b>'); // '' - invalid characters
 * sanitizeUrlParameter('account\nid'); // '' - contains newline
 * ```
 */
export function sanitizeUrlParameter(paramValue: string): string {
  if (!paramValue) {
    return '';
  }

  // First, check if it contains only valid characters
  if (!isValidGuidFormat(paramValue)) {
    return '';
  }

  // Additional safety: sanitize any HTML that might have passed
  const sanitized = sanitizeHtmlContent(paramValue);

  // Verify it still matches valid format after sanitization
  if (!isValidGuidFormat(sanitized)) {
    return '';
  }

  return sanitized;
}

/**
 * Validates and sanitizes an accountId parameter.
 * AccountIds should be valid GUIDs or alphanumeric identifiers.
 * Returns empty string if the accountId contains suspicious characters.
 *
 * @param accountId - The accountId to validate and sanitize
 * @returns The sanitized accountId or empty string if invalid
 *
 * @example
 * ```typescript
 * sanitizeAccountId('12345678-1234-1234-1234-123456789012'); // valid GUID
 * sanitizeAccountId('<hr>'); // '' - contains HTML tag
 * sanitizeAccountId('account/test'); // '' - contains slash
 * ```
 */
export function sanitizeAccountId(accountId: string): string {
  return sanitizeUrlParameter(accountId);
}

/**
 * Validates and sanitizes an invitationId parameter.
 * InvitationIds should be numeric values.
 * Returns empty string if the invitationId contains non-numeric characters.
 *
 * @param invitationId - The invitationId to validate and sanitize
 * @returns The sanitized invitationId or empty string if invalid
 *
 * @example
 * ```typescript
 * sanitizeInvitationId('12345'); // '12345'
 * sanitizeInvitationId('<script>'); // '' - contains non-numeric characters
 * ```
 */
export function sanitizeInvitationId(invitationId: string): string {
  if (!invitationId) {
    return '';
  }

  // InvitationId should be numeric only
  const numericPattern = /^\d+$/;
  if (!numericPattern.test(invitationId)) {
    return '';
  }

  return invitationId;
}
